// src/components/GlowCard.tsx
import React, { useRef, useCallback } from 'react';

interface GlowCardProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  glowColor?: string;
  bgGlowColor?: string;
}

const GlowCard: React.FC<GlowCardProps> = ({
  children,
  className = '',
  contentClassName = '',
  glowColor = 'rgba(168, 85, 247, 0.45)',
  bgGlowColor = 'rgba(99, 102, 241, 0.08)',
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    cardRef.current.style.setProperty('--x', `${e.clientX - rect.left}px`);
    cardRef.current.style.setProperty('--y', `${e.clientY - rect.top}px`);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.style.setProperty('--glow-opacity', '1');
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.style.setProperty('--glow-opacity', '0');
    }
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative rounded-xl overflow-x-clip p-[1px] transition-all duration-300 shadow-2xl ${className}`}
      style={{
        background: `radial-gradient(280px circle at var(--x, -500px) var(--y, -500px), ${glowColor}, rgba(168, 85, 247, 0.15) 60%, rgba(168, 85, 247, 0.05) 100%)`,
      }}
    >
      {/* Background radial spotlight */}
      <div
        className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-300"
        style={{
          opacity: 'var(--glow-opacity, 0)',
          background: `radial-gradient(350px circle at var(--x, -500px) var(--y, -500px), ${bgGlowColor}, transparent 80%)`,
        }}
      />

      {/* Inner solid content layer */}
      <div
        className={`relative z-10 w-full h-full bg-white/80 dark:bg-[#0e0d15]/85 border border-black/5 dark:border-transparent backdrop-blur-xl rounded-[11px] flex flex-col justify-between ${contentClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default React.memo(GlowCard);

